"use client";

import { useClerk, useSession } from "@clerk/nextjs";
import { useCallback, useEffect, useRef } from "react";

const INACTIVITY_MS = 24 * 60 * 60 * 1000; // 24 hours

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

export function useInactivityLogout() {
  const { session } = useSession();
  const { signOut } = useClerk();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleLogout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      signOut({ redirectUrl: "/sign-in" });
    }, INACTIVITY_MS);
  }, [signOut]);

  const onActivity = useCallback(() => {
    scheduleLogout();
  }, [scheduleLogout]);

  useEffect(() => {
    if (!session) return;

    const lastActiveAt = session.lastActiveAt
      ? new Date(session.lastActiveAt).getTime()
      : 0;
    const now = Date.now();
    if (lastActiveAt && now - lastActiveAt >= INACTIVITY_MS) {
      signOut({ redirectUrl: "/sign-in" });
      return;
    }

    scheduleLogout();

    const opts = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, opts);
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity, opts);
      });
    };
  }, [session, onActivity, scheduleLogout, signOut]);
}
